import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Loader2, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getGenresForBookType, getThemesForGenre } from '../constants/genreThemes';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const EditMetadataForm = ({ manuscript, onClose, onSuccess }) => {
  const [updating, setUpdating] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [author, setAuthor] = useState('');
  const [publisher, setPublisher] = useState('');
  const [isbn, setIsbn] = useState('');
  const [genre, setGenre] = useState(undefined);
  const [theme, setTheme] = useState(undefined);
  const [purchaseUrl, setPurchaseUrl] = useState('');

  useEffect(() => {
    if (manuscript) {
      setTitle(manuscript.title || '');
      setSubtitle(manuscript.subtitle || '');
      setAuthor(manuscript.author || '');
      setPublisher(manuscript.publisher || '');
      setIsbn(manuscript.isbn || '');
      setGenre(manuscript.genre);
      setTheme(manuscript.theme);
      setPurchaseUrl(manuscript.purchase_url || '');
    }
  }, [manuscript]);

  const handleCoverImageSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(selectedFile.type)) {
      toast.error('Please select a valid image file (jpg, png, webp)');
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('Cover image size exceeds 5MB limit');
      return;
    }

    setCoverImage(selectedFile);
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleUpdate = async () => {
    if (!title || !author) {
      toast.error('Title and Author are required');
      return;
    }

    setUpdating(true);

    try {
      const formData = new FormData();
      formData.append('title', title);
      if (subtitle) formData.append('subtitle', subtitle);
      formData.append('author', author);
      if (publisher) formData.append('publisher', publisher);
      if (isbn) formData.append('isbn', isbn);
      if (genre) formData.append('genre', genre);
      if (theme) formData.append('theme', theme);
      if (purchaseUrl) formData.append('purchase_url', purchaseUrl);
      if (coverImage) formData.append('cover', coverImage);

      await axios.patch(`${API}/manuscripts/${manuscript.id}/metadata`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Metadata updated successfully!');
      
      if (onSuccess) {
        onSuccess();
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update metadata');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Card className="shadow-lg bg-white border-0">
      <CardHeader>
        <div className="flex items-center gap-3 mb-3">
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-12 w-auto object-contain"
          />
        </div>
        <CardTitle className="flex items-center gap-2 text-xl text-gray-800">
          <Edit className="w-5 h-5 text-blue-600" />
          Edit Book Metadata
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Metadata Fields */}
        <div>
          <Label htmlFor="edit-title" className="text-sm font-semibold text-gray-700">
            Title <span className="text-red-500">*</span>
          </Label>
          <Input
            id="edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter book title"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="edit-subtitle" className="text-sm font-semibold text-gray-700">
            Subtitle
          </Label>
          <Input
            id="edit-subtitle"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            placeholder="Enter subtitle (optional)"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="edit-author" className="text-sm font-semibold text-gray-700">
            Author <span className="text-red-500">*</span>
          </Label>
          <Input
            id="edit-author"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder="Enter author name"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="edit-publisher" className="text-sm font-semibold text-gray-700">
            Publisher
          </Label>
          <Input
            id="edit-publisher"
            value={publisher}
            onChange={(e) => setPublisher(e.target.value)}
            placeholder="Enter publisher name (optional)"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="edit-isbn" className="text-sm font-semibold text-gray-700">
            ISBN
          </Label>
          <Input
            id="edit-isbn"
            value={isbn}
            onChange={(e) => setIsbn(e.target.value)}
            placeholder="Enter ISBN (e.g., 978-0-123456-78-9)"
            className="mt-1"
          />
          <p className="text-xs text-gray-500 mt-1">
            International Standard Book Number (ISBN-10 or ISBN-13)
          </p>
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Genre
          </Label>
          <Select value={genre || ''} onValueChange={(val) => {
            setGenre(val || undefined);
            setTheme(undefined); // Reset theme when genre changes
          }}>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Select genre (optional)" />
            </SelectTrigger>
            <SelectContent>
              {getGenresForBookType(manuscript.book_type).filter(opt => opt.value !== '').map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="edit-theme" className="text-sm font-semibold text-gray-700">
            Theme
          </Label>
          <select
            id="edit-theme"
            value={theme || ''}
            onChange={(e) => setTheme(e.target.value || undefined)}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {getThemesForGenre(genre || '').map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="edit-purchase-url" className="text-sm font-semibold text-gray-700">
            Purchase URL
          </Label>
          <Input
            id="edit-purchase-url"
            type="url"
            value={purchaseUrl}
            onChange={(e) => setPurchaseUrl(e.target.value)}
            placeholder="https://example.com/book (optional)"
            className="mt-1"
          />
        </div>

        {/* Cover Image Upload */}
        <div>
          <Label className="text-sm font-semibold text-gray-700 mb-2 block">
            Cover Image
          </Label>
          <div className="flex gap-4">
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors cursor-pointer bg-gray-50 flex-1"
              onClick={() => document.getElementById('cover-input-edit').click()}
            >
              {coverPreview ? (
                <div className="space-y-2">
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="w-24 h-36 object-cover mx-auto rounded"
                  />
                  <p className="text-xs text-gray-600">{coverImage?.name}</p>
                </div>
              ) : manuscript?.cover_image_path ? (
                <div className="space-y-2">
                  <img
                    src={`${BACKEND_URL}/api/covers/${manuscript.id}`}
                    alt="Current cover"
                    className="w-24 h-36 object-cover mx-auto rounded"
                  />
                  <p className="text-xs text-gray-600">Current cover</p>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-xs font-medium text-gray-700 mb-1">
                    Click to upload cover
                  </p>
                  <p className="text-xs text-gray-500">
                    .jpg, .png, .webp (max 5 MB)
                  </p>
                </>
              )}
              <input
                id="cover-input-edit"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleCoverImageSelect}
                className="hidden"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={updating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={updating || !title || !author}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          >
            {updating ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Updating...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Edit className="w-4 h-4" />
                Update Metadata
              </span>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EditMetadataForm;
